import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Sparkles, Loader2, CheckCircle2, AlertTriangle, XCircle, HelpCircle,
  Factory, Users, FileText, ArrowRight, Paperclip,
} from 'lucide-react';
import { aiReviewBulkStart, aiReviewBulkStatus, aiApplyCorrections } from '../services/api';
import { toast } from 'sonner';

const VERDICTS = {
  valido: { label: 'Sem divergências', cls: 'bg-green-50 text-green-700 border-green-200', Icon: CheckCircle2 },
  atencao: { label: 'Atenção', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
  divergente: { label: 'Divergências', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: XCircle },
  ilegivel: { label: 'Ilegível', cls: 'bg-gray-50 text-gray-600 border-gray-300', Icon: HelpCircle },
};
const CONF = { alta: 'Alta', media: 'Média', baixa: 'Baixa' };

const fmt = (v) => {
  if (v === true) return 'Indeterminado';
  if (v === false || v === null || v === undefined || v === '') return '—';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) { try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; } }
  return s;
};

const AIReviewDialog = ({ open, onOpenChange, defaultCategory = 'all', onApplied }) => {
  const [category, setCategory] = useState(defaultCategory === 'all' ? 'all' : defaultCategory);
  const [run, setRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const [selected, setSelected] = useState({});
  const [applying, setApplying] = useState({});
  const [applied, setApplied] = useState({});
  const pollRef = useRef(null);

  const stopPoll = useCallback(() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, []);

  const reset = useCallback(() => {
    stopPoll();
    setRun(null); setStarting(false); setSelected({}); setApplying({}); setApplied({});
  }, [stopPoll]);

  useEffect(() => () => stopPoll(), [stopPoll]);

  // default-select changes with confidence alta/media when items arrive
  useEffect(() => {
    if (!run?.items) return;
    setSelected((prev) => {
      const next = { ...prev };
      run.items.forEach((it) => {
        (it.proposed_changes || []).forEach((c) => {
          const key = `${it.license_id}:${c.field}`;
          if (!(key in next)) next[key] = c.confidence !== 'baixa';
        });
      });
      return next;
    });
  }, [run]);

  const startRun = async () => {
    setStarting(true);
    try {
      const { data } = await aiReviewBulkStart(category === 'all' ? null : category);
      const runId = data.run_id;
      setRun({ id: runId, status: 'running', total: 0, processed: 0, items: [] });
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await aiReviewBulkStatus(runId);
          setRun(st);
          if (st.status === 'done' || st.status === 'error') stopPoll();
        } catch { /* keep polling */ }
      }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao iniciar a revisão com IA');
    } finally {
      setStarting(false);
    }
  };

  const toggle = (licenseId, field) => {
    const key = `${licenseId}:${field}`;
    setSelected((p) => ({ ...p, [key]: !p[key] }));
  };

  const applyLicense = async (item) => {
    const changes = {};
    (item.proposed_changes || []).forEach((c) => {
      if (selected[`${item.license_id}:${c.field}`]) changes[c.field] = c.suggested;
    });
    if (Object.keys(changes).length === 0) { toast.error('Selecione ao menos uma correção'); return; }
    setApplying((p) => ({ ...p, [item.license_id]: true }));
    try {
      await aiApplyCorrections(item.license_id, changes);
      setApplied((p) => ({ ...p, [item.license_id]: true }));
      toast.success(`Correções aplicadas em "${item.name}"`);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aplicar correções');
    } finally {
      setApplying((p) => ({ ...p, [item.license_id]: false }));
    }
  };

  const items = run?.items || [];
  const withChanges = items.filter((it) => (it.proposed_changes || []).length > 0 || it.error);
  const noChanges = items.length - withChanges.length;
  const done = run && (run.status === 'done' || run.status === 'error');
  const pct = run && run.total ? Math.round((run.processed / run.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-white border-gray-200 sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="ai-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#003366]" /> Validar e corrigir licenças com IA
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            A IA (Gemini) lê o documento anexado de cada licença e sugere correções. Revise e aplique apenas o que aprovar — nada é alterado sem sua confirmação.
          </DialogDescription>
        </DialogHeader>

        {!run ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Quais licenças revisar?</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-700" data-testid="ai-review-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all">Todas as licenças</SelectItem>
                  <SelectItem value="kn">Apenas Kuehne+Nagel</SelectItem>
                  <SelectItem value="fornecedor">Apenas Fornecedores</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              A revisão roda em segundo plano e pode levar alguns minutos dependendo da quantidade de anexos. Licenças sem documento anexado passam por uma checagem de consistência.
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Progress */}
            <div className="space-y-1" data-testid="ai-review-progress">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span className="flex items-center gap-2">
                  {!done && <Loader2 className="w-4 h-4 animate-spin text-[#003366]" />}
                  {done ? 'Revisão concluída' : 'Revisando...'}
                </span>
                <span>{run.processed}/{run.total || '…'}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full bg-[#003366] transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>

            {/* Results */}
            {withChanges.length === 0 && done && (
              <div className="text-center py-8 text-gray-500" data-testid="ai-review-empty">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500" />
                Nenhuma correção sugerida. {noChanges > 0 ? `${noChanges} licença(s) revisada(s).` : ''}
              </div>
            )}

            <div className="space-y-3">
              {withChanges.map((it) => {
                const v = VERDICTS[it.verdict] || VERDICTS.ilegivel;
                const isApplied = applied[it.license_id];
                return (
                  <div key={it.license_id} className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid={`ai-review-item-${it.license_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText className="w-4 h-4 text-[#003366]" />
                      <span className="font-semibold text-gray-900 text-sm">{it.name}</span>
                      <Badge variant="outline" className={it.category === 'kn' ? 'bg-blue-50 text-[#003366] border-blue-200 text-xs' : 'bg-gray-100 text-gray-700 border-gray-300 text-xs'}>
                        {it.category === 'kn' ? <Factory className="w-3 h-3 mr-1" /> : <Users className="w-3 h-3 mr-1" />}
                        {it.category === 'kn' ? 'KN' : 'Fornecedor'}
                      </Badge>
                      {it.verdict && (
                        <Badge variant="outline" className={`${v.cls} text-xs`}>
                          <v.Icon className="w-3 h-3 mr-1" /> {v.label}
                        </Badge>
                      )}
                      {it.used_attachment && (
                        <span className="text-xs text-gray-400 flex items-center gap-1"><Paperclip className="w-3 h-3" /> via documento</span>
                      )}
                    </div>

                    {it.error ? (
                      <p className="text-xs text-rose-600">{it.error}</p>
                    ) : (
                      <>
                        <div className="space-y-1.5" data-testid={`ai-changes-${it.license_id}`}>
                          {(it.proposed_changes || []).map((c) => {
                            const key = `${it.license_id}:${c.field}`;
                            return (
                              <label key={c.field} className="flex items-start gap-2 text-sm cursor-pointer rounded-md hover:bg-gray-50 p-1.5">
                                <Checkbox
                                  checked={!!selected[key]}
                                  onCheckedChange={() => toggle(it.license_id, c.field)}
                                  disabled={isApplied}
                                  className="mt-0.5"
                                  data-testid={`ai-change-check-${it.license_id}-${c.field}`}
                                />
                                <span className="flex-1">
                                  <span className="font-medium text-gray-800">{c.label}</span>
                                  {c.confidence && <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">conf. {CONF[c.confidence] || c.confidence}</span>}
                                  <span className="flex items-center gap-2 text-xs mt-0.5">
                                    <span className="text-gray-400 line-through">{fmt(c.current)}</span>
                                    <ArrowRight className="w-3 h-3 text-gray-400" />
                                    <span className="text-[#003366] font-semibold">{fmt(c.suggested)}</span>
                                  </span>
                                  {c.reason && <span className="block text-[11px] text-gray-400">{c.reason}</span>}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="flex justify-end">
                          {isApplied ? (
                            <span className="text-xs text-green-600 flex items-center gap-1" data-testid={`ai-applied-${it.license_id}`}>
                              <CheckCircle2 className="w-4 h-4" /> Correções aplicadas
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => applyLicense(it)}
                              disabled={applying[it.license_id]}
                              className="bg-[#003366] hover:bg-[#002244] text-xs"
                              data-testid={`ai-apply-btn-${it.license_id}`}
                            >
                              {applying[it.license_id] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                              Aplicar selecionadas
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          {!run ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-white border-gray-300 text-gray-700">Cancelar</Button>
              <Button onClick={startRun} disabled={starting} className="bg-[#003366] hover:bg-[#002244]" data-testid="ai-review-start-btn">
                {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Iniciar revisão
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} className="bg-white border-gray-300 text-gray-700" data-testid="ai-review-close-btn">
              {done ? 'Concluir' : 'Fechar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIReviewDialog;
